import React, { useState, useEffect, useMemo } from 'react';
import { X, ChevronLeft, ChevronRight, ShoppingCart, Package, Search } from 'lucide-react';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import { useBcv } from '../context/BcvContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const getImageUrl = (url) => {
  if (url && (url.startsWith('/uploads') || url.startsWith('/api/uploads'))) {
    return `${BACKEND_URL}${url}`;
  }
  return url;
};

const getTotalStock = (tallas) => {
  if (!tallas || typeof tallas !== 'object') return 0;
  return Object.values(tallas).reduce((sum, v) => sum + (v || 0), 0);
};

export const ProductLightbox = ({ product, onClose, onAddToCart }) => {
  const { rate, toBolivares } = useBcv();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [selectedSize, setSelectedSize] = useState(null);

  const images = useMemo(() => product?.images || [], [product]);
  const tallas = useMemo(() => product?.tallas || {}, [product]);
  const availableSizes = useMemo(() => Object.entries(tallas).filter(([, s]) => s > 0), [tallas]);
  const totalStock = useMemo(() => getTotalStock(tallas), [tallas]);

  useEffect(() => {
    if (availableSizes.length === 1) setSelectedSize(availableSizes[0][0]);
    else setSelectedSize(null);
    setQuantity(1);
  }, [availableSizes]);

  const goToPrevious = () => {
    if (images.length > 1) { setCurrentIndex(prev => (prev === 0 ? images.length - 1 : prev - 1)); }
  };

  const goToNext = () => {
    if (images.length > 1) { setCurrentIndex(prev => (prev === images.length - 1 ? 0 : prev + 1)); }
  };

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') goToPrevious();
      if (e.key === 'ArrowRight') goToNext();
    };
    window.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [onClose, goToPrevious, goToNext]);

  const handleAddToCart = () => {
    if (!selectedSize || !onAddToCart) return;
    for (let i = 0; i < quantity; i++) {
      onAddToCart(product, selectedSize);
    }
    onClose();
  };

  if (!product) return null;
  const currentImage = images.length > 0 ? getImageUrl(images[currentIndex]) : null;
  const selectedStock = selectedSize ? (tallas[selectedSize] || 0) : 0;
  const isOferta = product.oferta === true;
  const precioFinal = isOferta && product.precio_oferta ? product.precio_oferta : product.precio;

  const SizeSelector = () => (
    <div>
      <span className="text-xs text-gray-400 uppercase tracking-wider">Elige tu talla</span>
      <div className="flex flex-wrap gap-2 mt-2">
        {availableSizes.map(([size, stock]) => (
          <button key={size} onClick={() => { setSelectedSize(size); setQuantity(1); }}
            data-testid={`lightbox-size-${size}`}
            className={`px-4 py-2 rounded-lg text-sm font-bold transition-all ${
              selectedSize === size
                ? 'bg-[#39FF14] text-black shadow-[0_0_8px_rgba(57,255,20,0.5)]'
                : 'bg-[#1a1a1a] border border-gray-700 text-white hover:border-[#39FF14]/50'
            }`}>
            {size}
            <span className={`ml-1 text-xs ${selectedSize === size ? 'text-black/60' : 'text-gray-500'}`}>({stock})</span>
          </button>
        ))}
      </div>
      {!selectedSize && availableSizes.length > 1 && (
        <p className="text-xs text-[#FF9500] mt-2">Selecciona una talla para continuar</p>
      )}
    </div>
  );

  return (
    <div className="fixed inset-0 z-[100]" data-testid="product-lightbox">
      <div className="absolute inset-0 bg-black" onClick={onClose} />

      {/* ===== MOBILE (< lg) ===== */}
      <div className="relative z-10 flex flex-col h-full lg:hidden bg-[#0a0a0a]">
        <div className="flex-shrink-0 flex items-center justify-between px-4 py-3 border-b border-gray-800/50">
          <h2 className="text-sm font-bold text-white uppercase truncate pr-3" data-testid="lightbox-title-mobile">{product.descripcion}</h2>
          <button onClick={onClose} data-testid="lightbox-close" className="p-1.5 rounded-full bg-white/10 text-white flex-shrink-0"><X className="h-5 w-5" /></button>
        </div>

        <div className="flex-1 overflow-y-auto overscroll-contain">
          <div className="relative w-full bg-black">
            {currentImage ? (
              <div className="relative w-full aspect-[4/3]">
                <TransformWrapper initialScale={1} minScale={1} maxScale={4} wheel={{ step: 0.1 }} doubleClick={{ step: 0.5 }}>
                  <TransformComponent wrapperStyle={{ width: "100%", height: "100%" }} contentStyle={{ width: "100%", height: "100%" }}>
                    <img src={currentImage} alt={product.descripcion} className="w-full h-full object-contain" draggable={false} />
                  </TransformComponent>
                </TransformWrapper>
                <div className="absolute bottom-2 right-2 px-2 py-1 rounded bg-black/60 text-white text-[10px] flex items-center gap-1 pointer-events-none">
                  <Search className="h-3 w-3" /> Pellizca para zoom
                </div>
              </div>
            ) : (
              <div className="w-full aspect-[4/3] flex items-center justify-center bg-[#111]"><Package className="h-12 w-12 text-gray-600" /></div>
            )}
            {images.length > 1 && (
              <>
                <button onClick={goToPrevious} className="absolute left-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/50 text-white"><ChevronLeft className="h-5 w-5" /></button>
                <button onClick={goToNext} className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/50 text-white"><ChevronRight className="h-5 w-5" /></button>
              </>
            )}
          </div>

          {images.length > 1 && (
            <div className="flex gap-2 px-4 py-3 bg-[#0a0a0a] overflow-x-auto" data-testid="lightbox-thumbnails">
              {images.map((img, index) => (
                <button key={img} onClick={() => { setCurrentIndex(index); }}
                  className={`flex-shrink-0 w-14 h-14 rounded-md overflow-hidden border-2 transition-all ${
                    index === currentIndex ? 'border-[#39FF14]' : 'border-gray-700 opacity-50'}`}>
                  <img src={getImageUrl(img)} alt={`${index + 1}`} className="w-full h-full object-cover" />
                </button>
              ))}
              <div className="flex-shrink-0 flex items-center pl-1"><span className="text-[10px] text-gray-500 whitespace-nowrap">{currentIndex + 1}/{images.length}</span></div>
            </div>
          )}

          <div className="px-4 py-4 space-y-4">
            {/* Badges en lightbox */}
            <div className="flex items-center gap-2">
              {isOferta && (
                <span className="bg-gradient-to-r from-[#FF003C] to-[#FF6B00] text-white font-black text-xs px-3 py-1 rounded-full oferta-badge">OFERTA</span>
              )}
              {product.descuento > 0 && (
                <span className="bg-[#FFD700] text-black font-black text-xs px-2 py-1 rounded-full">
                  -{product.descuento}% OFF
                </span>
              )}
            </div>
            <div className="flex items-center justify-between">
              {product.precio_oferta ? (
                <div data-testid="lightbox-price">
                  <span className="text-gray-500 text-sm line-through mr-2">${(product.precio || 0).toFixed(2)}</span>
                  <span className={`text-2xl font-black ${isOferta ? 'text-[#FF003C]' : 'text-[#39FF14]'}`}>
                    ${product.precio_oferta.toFixed(2)}
                    <span className="text-xs text-gray-400 font-normal ml-1">USD</span>
                  </span>
                  {toBolivares(product.precio_oferta) && (
                    <div className="text-sm text-gray-400 font-semibold mt-0.5">
                      Bs. {toBolivares(product.precio_oferta).toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      {rate && <span className="text-[10px] text-gray-600 ml-1">(Tasa BCV: {rate.toLocaleString('es-VE', { minimumFractionDigits: 2 })})</span>}
                    </div>
                  )}
                </div>
              ) : (
                <div data-testid="lightbox-price">
                  <span className="text-2xl font-black text-[#39FF14]">${(product.precio || 0).toFixed(2)} <span className="text-xs text-gray-400 font-normal">USD</span></span>
                  {toBolivares(product.precio) && (
                    <div className="text-sm text-gray-400 font-semibold mt-0.5">
                      Bs. {toBolivares(product.precio).toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      {rate && <span className="text-[10px] text-gray-600 ml-1">(Tasa BCV: {rate.toLocaleString('es-VE', { minimumFractionDigits: 2 })})</span>}
                    </div>
                  )}
                </div>
              )}
            </div>
            <SizeSelector />
            <div className="flex items-center justify-between text-xs">
              <div className="flex items-center gap-1.5">
                <span className={`w-2 h-2 rounded-full ${totalStock > 0 ? 'bg-[#39FF14]' : 'bg-red-500'}`} />
                <span className={totalStock > 0 ? 'text-white' : 'text-red-400'}>
                  {selectedSize ? `${selectedStock} en stock (talla ${selectedSize})` : `${totalStock} unidades totales`}
                </span>
              </div>
              <div className="flex items-center gap-1 text-gray-500">
                <span className="w-3 h-2 bg-[#FFCC00] rounded-sm" /><span className="w-3 h-2 bg-[#FF0000] rounded-sm" />
                <span className="text-[10px]">Pago Móvil</span>
              </div>
            </div>
          </div>
        </div>

        {totalStock > 0 && (
          <div className="flex-shrink-0 px-4 py-3 bg-[#0a0a0a] border-t border-gray-800/50 flex items-center gap-3">
            {selectedSize && (
              <div className="flex items-center gap-2 bg-[#1a1a1a] rounded-lg px-2 py-1.5 border border-gray-700">
                <button onClick={() => setQuantity(Math.max(1, quantity - 1))} className="w-8 h-8 flex items-center justify-center text-white font-bold text-lg">-</button>
                <span className="text-white font-bold min-w-[20px] text-center">{quantity}</span>
                <button onClick={() => setQuantity(Math.min(selectedStock, quantity + 1))} className="w-8 h-8 flex items-center justify-center text-white font-bold text-lg">+</button>
              </div>
            )}
            <button onClick={handleAddToCart} disabled={!selectedSize} data-testid="lightbox-add-to-cart"
              className={`flex-1 py-3 rounded-xl font-black text-sm flex items-center justify-center gap-2 transition-all active:scale-[0.98] ${
                selectedSize ? 'bg-[#39FF14] text-black shadow-[0_0_12px_rgba(57,255,20,0.4)]' : 'bg-gray-700 text-gray-400 cursor-not-allowed'}`}>
              <ShoppingCart className="h-4 w-4" />
              {selectedSize
                ? `AÑADIR $${(precioFinal * quantity).toFixed(2)} USD${toBolivares(precioFinal * quantity) ? ` / Bs. ${toBolivares(precioFinal * quantity).toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : ''}`
                : 'SELECCIONA TALLA'}
            </button>
          </div>
        )}
      </div>

      {/* ===== DESKTOP (>= lg) ===== */}
      <div className="relative z-10 hidden lg:flex h-full items-center justify-center p-8">
        <button onClick={onClose} data-testid="lightbox-close-desktop" className="absolute top-4 right-4 z-50 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white"><X className="h-6 w-6" /></button>
        <div className="flex max-w-[1100px] w-full max-h-[85vh] gap-8">
          <div className="flex-1 flex flex-col items-center justify-center min-w-0">
            <div className="relative w-full max-w-[550px] aspect-square">
              {currentImage ? (
                <div className="relative w-full h-full rounded-xl overflow-hidden bg-[#0a0a0a]" data-testid="lightbox-main-image">
                  <TransformWrapper initialScale={1} minScale={1} maxScale={4} wheel={{ step: 0.1 }}>
                    <TransformComponent wrapperStyle={{ width: "100%", height: "100%", cursor: "grab" }} contentStyle={{ width: "100%", height: "100%" }}>
                      <img src={currentImage} alt={product.descripcion} className="w-full h-full object-contain" draggable={false} />
                    </TransformComponent>
                  </TransformWrapper>
                  <div className="absolute bottom-3 right-3 px-2.5 py-1.5 rounded-lg bg-black/70 text-white text-xs flex items-center gap-1.5 pointer-events-none">
                    <Search className="h-3.5 w-3.5" /> Rueda del mouse para zoom
                  </div>
                </div>
              ) : (
                <div className="w-full h-full rounded-xl bg-[#1a1a1a] flex items-center justify-center"><Package className="h-16 w-16 opacity-30 text-gray-500" /></div>
              )}
              {images.length > 1 && (
                <>
                  <button onClick={goToPrevious} className="absolute left-3 top-1/2 -translate-y-1/2 p-2.5 rounded-full bg-black/60 hover:bg-black/80 text-white"><ChevronLeft className="h-5 w-5" /></button>
                  <button onClick={goToNext} className="absolute right-3 top-1/2 -translate-y-1/2 p-2.5 rounded-full bg-black/60 hover:bg-black/80 text-white"><ChevronRight className="h-5 w-5" /></button>
                </>
              )}
            </div>
            {images.length > 1 && (
              <div className="flex gap-2 mt-4">
                {images.map((img, index) => (
                  <button key={img} onClick={() => { setCurrentIndex(index); }}
                    className={`w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${index === currentIndex ? 'border-[#39FF14]' : 'border-transparent opacity-60 hover:opacity-100'}`}>
                    <img src={getImageUrl(img)} alt={`${index + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="w-[360px] flex-shrink-0 overflow-y-auto space-y-5 py-4">
            <h2 className="text-2xl font-black text-white uppercase leading-tight" data-testid="lightbox-title">{product.descripcion}</h2>
            {/* Badges desktop */}
            <div className="flex items-center gap-2">
              {isOferta && (
                <span className="bg-gradient-to-r from-[#FF003C] to-[#FF6B00] text-white font-black text-xs px-3 py-1.5 rounded-full oferta-badge">OFERTA</span>
              )}
              {product.descuento > 0 && (
                <span className="bg-[#FFD700] text-black font-black text-xs px-2 py-1.5 rounded-full">
                  -{product.descuento}% OFF
                </span>
              )}
            </div>
            {product.precio_oferta ? (
              <div className="flex flex-col">
                <div className="flex items-center gap-2">
                  <span className="text-gray-500 text-lg line-through font-bold">${(product.precio || 0).toFixed(2)}</span>
                  {product.descuento > 0 && (
                    <span className="bg-[#39FF14] text-black text-[10px] font-black px-2 py-0.5 rounded italic">AHORRAS {product.descuento}%</span>
                  )}
                </div>
                <div>
                  <span className={`text-4xl font-black ${isOferta ? 'text-[#FF003C]' : 'text-[#39FF14]'}`}>
                    ${product.precio_oferta.toFixed(2)}
                  </span>
                  <span className="text-sm text-gray-400 ml-2">USD</span>
                </div>
                {toBolivares(product.precio_oferta) && (
                  <div className="mt-1">
                    <span className="text-lg text-gray-300 font-bold">Bs. {toBolivares(product.precio_oferta).toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    {rate && <span className="block text-[11px] text-gray-600">Tasa BCV: {rate.toLocaleString('es-VE', { minimumFractionDigits: 2 })}</span>}
                  </div>
                )}
              </div>
            ) : (
              <div>
                <span className="text-4xl font-black text-[#39FF14]">${(product.precio || 0).toFixed(2)}</span>
                <span className="text-sm text-gray-400 ml-2">USD</span>
                {toBolivares(product.precio) && (
                  <div className="mt-1">
                    <span className="text-lg text-gray-300 font-bold">Bs. {toBolivares(product.precio).toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    {rate && <span className="block text-[11px] text-gray-600">Tasa BCV: {rate.toLocaleString('es-VE', { minimumFractionDigits: 2 })}</span>}
                  </div>
                )}
              </div>
            )}
            <div className="h-px bg-gray-800" />
            <SizeSelector />
            <div>
              <span className="text-xs text-gray-400 uppercase tracking-wider">Disponibilidad</span>
              <div className="mt-2 flex items-center gap-2">
                <span className={`w-2.5 h-2.5 rounded-full ${totalStock > 0 ? 'bg-[#39FF14] animate-pulse' : 'bg-red-500'}`} />
                <span className="text-sm text-white font-medium">{selectedSize ? `${selectedStock} en stock (talla ${selectedSize})` : `${totalStock} unidades totales`}</span>
              </div>
            </div>
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <span className="w-5 h-3.5 bg-[#FFCC00] rounded-sm" /><span className="w-5 h-3.5 bg-[#FF0000] rounded-sm" /><span>Pago Móvil disponible</span>
            </div>
            <div className="h-px bg-gray-800" />
            {totalStock > 0 && (
              <div className="space-y-3">
                {selectedSize && (
                  <div>
                    <span className="text-xs text-gray-400 uppercase tracking-wider">Cantidad</span>
                    <div className="flex items-center gap-3 mt-2">
                      <button onClick={() => setQuantity(Math.max(1, quantity - 1))} className="w-10 h-10 rounded-lg bg-[#1a1a1a] border border-gray-700 text-white font-bold flex items-center justify-center hover:border-[#39FF14]/50">-</button>
                      <span className="text-white font-bold text-lg min-w-[30px] text-center">{quantity}</span>
                      <button onClick={() => setQuantity(Math.min(selectedStock, quantity + 1))} className="w-10 h-10 rounded-lg bg-[#1a1a1a] border border-gray-700 text-white font-bold flex items-center justify-center hover:border-[#39FF14]/50">+</button>
                      <span className="text-xs text-gray-500 ml-2">
                        ${(precioFinal * quantity).toFixed(2)} USD
                        {toBolivares(precioFinal * quantity) && ` / Bs. ${toBolivares(precioFinal * quantity).toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                      </span>
                    </div>
                  </div>
                )}
                <button onClick={handleAddToCart} disabled={!selectedSize} data-testid="lightbox-add-to-cart-desktop"
                  className={`w-full py-3.5 rounded-xl font-black text-sm uppercase tracking-wider flex items-center justify-center gap-2 transition-all active:scale-[0.98] ${
                    selectedSize ? 'bg-[#39FF14] text-black shadow-[0_0_15px_rgba(57,255,20,0.4)] hover:shadow-[0_0_25px_rgba(57,255,20,0.6)]' : 'bg-gray-700 text-gray-400 cursor-not-allowed'}`}>
                  <ShoppingCart className="h-5 w-5" />
                  {selectedSize ? `AÑADIR AL CARRITO (${quantity})` : 'SELECCIONA UNA TALLA'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
